import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { challanKeys } from "@/features/challans/use-challan-list";
import { api } from "@/lib/api";
import {
  endpoints,
  type ChallanListResponse,
  type Customer,
  type CustomerListResponse,
  type DocumentPreview,
  type InvoiceDetail,
  type ItemListResponse,
  type PreviewEnvelope,
} from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";
import { paiseToInputValue, parseRupeesToPaise } from "@/lib/money";
import {
  basisPointsToInputValue,
  formatScaled,
  milliToInputValue,
  parseKgToGrams,
  parseMetresToMm,
  parsePercentToBasisPoints,
  parseQtyToMilli,
} from "@/lib/units";

/**
 * The invoice editor's data layer (§7).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FORM HOLDS STRINGS; THE API TAKES INTEGERS. Every numeric field on this
 * screen is stored as the TEXT the owner typed, and converted only when a
 * request is built. Two reasons, and both have bitten real invoicing software:
 *
 *  - A half-typed value has to be allowed to exist. Storing `1.` or `` as a
 *    number means deciding it is `1` or `0` while the owner is still typing,
 *    and a rate field that silently becomes 0 bills the customer for nothing.
 *  - Converting late means converting ONCE, through the integer-safe parsers in
 *    lib/units.ts. `Math.round(rate * 100)` scattered through a component is how
 *    a float reaches a legal document.
 *
 * NO TOTAL IS COMPUTED HERE. Not the line amount, not the tax, not the grand
 * total. Every figure on the screen comes from `POST /invoices/preview`, which
 * runs the same pipeline the save runs (§6, CLAUDE.md). A client-side "quick
 * preview" would be a second tax engine, and the day it disagreed with the
 * server the customer would find out first.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** One line, as the FORM holds it — every numeric field a string. */
export interface EditorLine {
  /** Stable within this document; the server derives `10`, `10-10` from it (§4). */
  key: string;
  parentKey: string | null;
  isGroupHeader: boolean;
  itemId: number | null;
  description: string;
  /**
   * Work vs material (§7.1). The server SNAPSHOTS this onto the line; before
   * the editor sent it, every custom line was silently stored as a works
   * contract — the tax on the bill was still right (the HSN and rate are typed
   * independently), but the stored classification was wrong for every traded
   * or fabricated good.
   */
  supplyNature: "WORKS_CONTRACT" | "FABRICATED_GOODS" | "TRADED_GOODS";
  hsn: string;
  /** Percent as typed: `"18"`, `"2.5"`. */
  gstRatePercent: string;
  pricingMode: "LUMPSUM" | "BY_PIECE" | "BY_WEIGHT" | "BY_LENGTH";
  /** Three decimals, as the documents print. */
  qty: string;
  unitLabel: string;
  /** Rupees as typed: `"6750"`, `"67.50"`. */
  rate: string;
  pieces: string;
  weightKg: string;
  lengthM: string;
  /**
   * §15(3)(a) — a discount on THIS line, in rupees as typed. Blank is the
   * ordinary case and means no discount. Read only when
   * {@link EditorLine.discountMode} is `"AMOUNT"`.
   */
  discount: string;
  /**
   * The SAME discount, stated as a human percentage — `"10"`, `"7.5"`. Read only
   * when {@link EditorLine.discountMode} is `"PERCENT"`, and converted to BASIS
   * POINTS at the wire edge by {@link discountBody}, never before.
   *
   * IT NEVER BECOMES PAISE HERE. §15(3)(a) requires the DISCOUNT — the money —
   * to be recorded in the invoice, and the server is the one authority that
   * turns "10%" into a figure (`resolveDiscountPercent`, services/documents.ts),
   * once, at save, through `divRoundHalfUp`. A client that resolved it first
   * would be a second tax engine and would disagree the day it rounded
   * differently (CLAUDE.md, §6).
   */
  discountPercent: string;
  /**
   * Which of the two boxes above IS the discount. Exactly one crosses the wire —
   * `discountPaise` XOR `discountPercentBp`; both together is a 400
   * (`DISCOUNT_PERCENT_AND_AMOUNT`), because two answers to "what is the
   * discount" means the server dropping one without saying so.
   *
   * The other box KEEPS ITS TEXT while it is not the answer, and is simply not
   * sent. Clearing it on every toggle would throw away a figure the owner typed
   * on a mis-tap, and a stale value cannot leak: {@link discountBody} reads the
   * mode first and only ever returns one key.
   */
  discountMode: DiscountMode;
}

/**
 * §15(3)(a) — which form a discount was typed in.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A PERCENTAGE IS AN INPUT; THE PAISE ARE THE RECORD.
 *
 * `"10%"` re-evaluated later against an edited line resolves to a different bill
 * from the one the customer accepted and the CA filed. So the percentage is
 * resolved to money ONCE, by the SERVER, at save, and it is that amount which is
 * taxed, stored and printed; the percentage is kept beside it as PROVENANCE and
 * is never re-applied on read by anything (`carryDiscountProvenance`).
 *
 * WHICH IS WHY A DOCUMENT LOADED BACK FROM THE SERVER ALWAYS OPENS IN `"AMOUNT"`,
 * whatever it was originally typed in — see {@link linesFromInvoice}. Re-sending
 * the stored percentage would make it a FRESH input, resolved against the lines
 * as they are now, silently moving a figure that was already agreed. Re-sending
 * the stored AMOUNT is what lets the server re-attach the percentage as
 * provenance and WARN when it no longer comes to that money.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export type DiscountMode = "AMOUNT" | "PERCENT";

/** The three fields a discount is held in — on a line, and on the document. */
export interface DiscountFields {
  discount: string;
  discountPercent: string;
  discountMode: DiscountMode;
}

export interface EditorState {
  customerId: number | null;
  date: string;
  soNumber: string;
  soDate: string;
  challanNumber: string;
  challanDate: string;
  /**
   * OUR delivery challans this bill closes (§17.7) — the FK, not the printed
   * reference, and the two live side by side deliberately.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * `challanNumber` ABOVE IS FREE TEXT AND STAYS THAT WAY. §6 says that column
   * most often holds the CUSTOMER's own challan reference — a document that is
   * not ours and that no id could name. This array is the other thing: ids of
   * rows in `delivery_challans`, which the save links and moves to INVOICED
   * inside the same transaction that allocates the invoice number.
   *
   * The server copies the linked challans' numbers into the printed text
   * itself; nothing here writes `challanNumber` from a selection, because the
   * 64-character cap and the "his reference or ours" question are the server's
   * to answer once, not this screen's to answer differently.
   *
   * EMPTY IS THE ORDINARY CASE. Most bills close no delivery, and `toApiBody`
   * omits the key entirely rather than sending `[]` — `challanIds` is
   * `.min(1)`, so an empty array is a 400.
   * ───────────────────────────────────────────────────────────────────────────
   */
  challanIds: number[];
  quotationRef: string;
  quotationRefDate: string;
  attnName: string;
  notes: string;
  terms: string;
  /**
   * §15(3)(a) — a discount on the WHOLE bill, in rupees as typed.
   *
   * IT IS NOT SUBTRACTED FROM THE TOTAL, and nothing on this screen subtracts
   * anything. The server apportions it across the lines in proportion to their
   * post-line-discount taxable values and recomputes each line's tax at its own
   * rate (§8.1: they differ), because taking it off the grand total instead
   * would print a wrong CGST and a wrong SGST on a legal document. What the
   * editor shows is whatever `POST /invoices/preview` answers.
   */
  discount: string;
  /**
   * §15(3)(a) — the same whole-bill discount as a human percentage, `"10"`. OF
   * THE SUBTOTAL AFTER THE PER-LINE DISCOUNTS, which is the base the amount form
   * is already applied to, so the two forms of one discount agree to the paisa.
   * Resolved by the server and nowhere else.
   */
  discountPercent: string;
  /** Which of the two above is sent — never both (§15(3)(a)). */
  discountMode: DiscountMode;
  lines: EditorLine[];
}

/**
 * An issued invoice's lines, back into the strings the editor holds — the
 * "Repeat Last Bill" / "Make a copy" mapping (§11.3).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * §11.3 calls this "the highest-value shortcut in the whole app": most of this
 * business's bills are the same few jobs for the same few customers. Both
 * buttons existed and NAVIGATED — to an editor that never read the parameter
 * and opened blank, which reads as the app having forgotten the bill.
 *
 * WHAT IS DELIBERATELY NOT COPIED: the date (today's bill gets today's date),
 * the S.O. / challan / quotation references (they identify the OLD order — a
 * repeat bill against last month's S.O. number is exactly the mismatch a
 * corporate AP team rejects), and the number (allocated fresh on save, §6).
 *
 * THE INTEGERS COME BACK THROUGH THE SAME SCALE HELPERS THE FORM PARSES WITH,
 * so a copied line round-trips exactly: 6750.00 stored as 675000 paise becomes
 * the string "6750" and parses back to 675000. Rates drop trailing zeros
 * ("18", not "18.00") because that is how the form is typed by hand.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function linesFromInvoice(
  /*
   * STRUCTURAL, NOT `InvoiceDetail` — a QUOTATION's lines are the same shape and
   * the quotation editor reuses this to open "edit" and "copy". Widening the
   * parameter is deliberate: the alternative was a second copy of this mapping,
   * and the two would drift the first time a line field was added, silently, on
   * whichever document kind was not being worked on that day.
   */
  invoice: { customerId: number; attnName: string | null; lines: InvoiceDetail["lines"] },
  nextKey: () => string,
): { customerId: number; attnName: string; lines: EditorLine[] } {
  const keyByLineId = new Map<number, string>();
  for (const line of invoice.lines) keyByLineId.set(line.id, nextKey());

  return {
    customerId: invoice.customerId,
    attnName: invoice.attnName ?? "",
    lines: invoice.lines.map((line) => ({
      key: keyByLineId.get(line.id) ?? nextKey(),
      parentKey: line.parentLineId === null ? null : (keyByLineId.get(line.parentLineId) ?? null),
      isGroupHeader: line.isGroupHeader,
      itemId: line.itemId,
      description: line.description,
      supplyNature: line.supplyNature as EditorLine["supplyNature"],
      hsn: line.hsn,
      gstRatePercent: basisPointsToInputValue(line.gstRateBp),
      pricingMode: line.pricingMode as EditorLine["pricingMode"],
      qty: milliToInputValue(line.qtyMilli),
      unitLabel: line.unitLabel,
      // A header's rate is DERIVED from its children (§9.1); copying it would
      // send a rate the server ignores at best and doubles at worst.
      rate: line.isGroupHeader ? "" : paiseToInputValue(line.ratePaise),
      pieces: line.pieces === null ? "" : String(line.pieces),
      weightKg: line.weightGrams === null ? "" : formatScaled(line.weightGrams, 3),
      lengthM: line.lengthMm === null ? "" : formatScaled(line.lengthMm, 3),
      /*
       * THE LINE'S OWN DISCOUNT COMES ACROSS; ITS APPORTIONED SHARE DOES NOT.
       *
       * `apportionedDiscountPaise` is a CONSEQUENCE of the old document's
       * whole-bill discount, computed against the lines that document had.
       * Copying it onto a line of a new bill would carry a share of a total
       * nobody has agreed to yet, and the engine would apportion the new
       * document's discount over it a second time. The whole-bill figure is
       * copied once, at document level, by the callers of this function — and
       * that is a CONTRACT, not a description. The invoice copy path
       * (`?copyFrom=`) did not honour it and dropped the whole-bill discount
       * while keeping the per-line ones, so half of §15(3)(a) travelled and half
       * did not, for no stated reason.
       *
       * THE CALLER MUST BE ABLE TO HOLD ONE. A credit note cannot — it has no
       * discount field of either kind — so it does not use this function at all;
       * see {@link creditNoteLinesFromInvoice}.
       */
      discount: discountInput(line.discountPaise),
      /*
       * AND IT COMES BACK AS AN AMOUNT, EVEN WHEN IT WAS TYPED AS A PERCENTAGE.
       *
       * `line.discountPercentBp` is on the wire and is deliberately NOT read
       * here. §15(3)(a) records the DISCOUNT — the money — and the percentage
       * beside it is provenance, "how that figure was reached". Seeding the box
       * with `10%` instead would make the next save a FRESH percentage,
       * resolved against the line as it is NOW: a repeat bill whose rate has
       * moved since would quietly give away a different amount from the one the
       * customer agreed to, with nothing on screen saying so.
       *
       * Sending the AMOUNT back is also what keeps the server's own machinery
       * working: `carryDiscountProvenance` re-attaches the stored percentage to
       * an unchanged amount and WARNS when it no longer comes to it. That
       * warning is the whole point, and a client that re-sent the percentage
       * would silence it by making it true.
       */
      discountPercent: "",
      discountMode: "AMOUNT" as const,
    })),
  };
}

/**
 * The same mapping, for a document that CANNOT carry a discount — a credit note
 * (§17.3, §15(3)(a)).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A CREDIT NOTE MUST REVERSE WHAT WAS BILLED, NOT WHAT WAS QUOTED BEFORE THE
 * DISCOUNT.
 *
 * `linesFromInvoice` copies `ratePaise`, which is the rate BEFORE anything came
 * off, and deliberately does not copy `apportionedDiscountPaise` (copying it
 * would let the engine apportion over it a second time). On an undiscounted bill
 * — every bill until §15(3)(a) shipped — those two facts are the same fact and
 * nothing is lost. On a discounted one the seeded lines reconstruct the
 * PRE-DISCOUNT invoice, and a credit note has no discount field of either kind
 * for the reduction to land in, so the note comes out larger than the bill it
 * reduces. The server then refuses it — "more than is outstanding", 409 — and
 * the owner has no way to credit back a bill the app itself let him discount.
 * On a PARTIAL credit it does not even refuse: it silently reverses more output
 * tax than was ever charged, against a taxable value that was never billed, and
 * is declared that way in GSTR-1 CDNR.
 *
 * So a DISCOUNTED line is seeded at the value of supply the invoice actually
 * charged tax on — `taxablePaise`, the server's own figure, already net of both
 * the line's own discount and its share of the whole-bill one.
 *
 * WHY IT BECOMES A LUMPSUM LINE OF ONE. The value has to come out EXACT: the
 * ceiling check compares the note against the bill to the paisa, and a note one
 * paisa over is a 409 nobody can explain. Reducing the RATE instead means
 * dividing the net value by the quantity (or by the weight, or the length),
 * which rounds — 29000 paise over qty 3.000 has no integer rate at all. A
 * LUMPSUM line at qty 1.000 is the one shape where the engine's taxable value IS
 * the rate, so the figure reproduces exactly, by construction, in every pricing
 * mode. The measurement columns are cleared with it, because they no longer
 * describe the number beside them; the description, HSN and the SNAPSHOT GST
 * rate (§8.7) all travel untouched, so the note still reverses the right tax at
 * the right rate.
 *
 * AN UNDISCOUNTED LINE IS UNTOUCHED — same object, same shape, same fields as
 * before this function existed. That is deliberate and is what keeps every
 * existing credit note, and every fixture, on exactly the path it was on.
 *
 * `discount` COMES OUT EMPTY ON EVERY LINE, and structurally so: a line with a
 * discount takes the branch below, which clears it. `toApiLine` then omits
 * `discountPaise` entirely — which matters, because `CreditNoteCreateSchema.lines`
 * is `LinesSchema` and `z.object()` STRIPS the key rather than refusing it. The
 * editor was sending a discount the client's own engine applied to the total on
 * screen and the server never saw, so the bar and the issued note disagreed by
 * the discount and its tax, on a document that is issued immediately, consumes
 * its number, and has no edit path.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function creditNoteLinesFromInvoice(
  invoice: { customerId: number; attnName: string | null; lines: InvoiceDetail["lines"] },
  nextKey: () => string,
): { customerId: number; attnName: string; lines: EditorLine[]; anyDiscount: boolean } {
  // Through the SHARED mapping first, so there is still exactly one place that
  // turns a stored integer back into the string the form holds.
  const seed = linesFromInvoice(invoice, nextKey);
  let anyDiscount = false;

  const lines = seed.lines.map((line, index) => {
    // Index-aligned: `linesFromInvoice` maps `invoice.lines` in order.
    const source = invoice.lines[index];
    if (source === undefined || line.isGroupHeader) return line;

    const cameOff = (source.discountPaise ?? 0) + (source.apportionedDiscountPaise ?? 0);
    if (cameOff === 0) return line;
    anyDiscount = true;

    // Already a lumpsum line of one? Then only the rate moves, and the unit the
    // owner chose is still true of it.
    const shapeKept = source.pricingMode === "LUMPSUM" && source.qtyMilli === 1000;

    return {
      ...line,
      pricingMode: "LUMPSUM" as const,
      qty: "1",
      unitLabel: shapeKept ? line.unitLabel : "NO",
      rate: paiseToInputValue(source.taxablePaise),
      pieces: "",
      weightKg: "",
      lengthM: "",
      // BOTH boxes, and the mode with them. A credit note carries no discount of
      // either kind (§17.3), and `toApiLine` decides which key to send from
      // `discountMode` — so leaving a percentage behind here would be the same
      // silent-strip defect one field along.
      discount: "",
      discountPercent: "",
      discountMode: "AMOUNT" as const,
    };
  });

  return { customerId: seed.customerId, attnName: seed.attnName, lines, anyDiscount };
}

/**
 * Line keys for one editor — and the reason they cannot simply be a counter.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A RESTORED DRAFT BRINGS ITS OLD KEYS WITH IT, AND THE COUNTER DOES NOT.
 *
 * The counter lives at module scope, so it restarts at 0 on every page load.
 * A draft saved yesterday holds lines keyed `Q1`, `Q2`; reopening it today the
 * initial `useState` burns `Q1` (counter → 1), the restored payload replaces
 * that state, and the very next "Add line" hands out `Q2` — a key one of the
 * restored lines already owns.
 *
 * From then on the two rows are the same row to every operation that matches on
 * key: `patchLine` maps over `lines` and rewrites BOTH, so typing a rate into
 * the new line silently retypes it into the old one, and `removeLine` deletes
 * the pair. `toApiLine` then sends two lines with the same `key`, and the
 * server derives §4's SR.NO hierarchy from exactly that field — so a parent
 * link can attach to the wrong row on a document that is about to be issued.
 *
 * `adopt` is what closes it: before restored lines go into state, the counter
 * is advanced past every number already in use. Called by both editors on the
 * resume path, and harmless (a no-op) for a brand-new document.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function makeKeyFactory(prefix: string): {
  next: () => string;
  adopt: (lines: readonly { key: string }[]) => void;
} {
  let counter = 0;
  return {
    next: () => {
      counter += 1;
      return `${prefix}${String(counter)}`;
    },
    adopt: (lines) => {
      for (const line of lines) {
        // The PREFIX IS IGNORED, only the trailing number is read. A payload
        // can carry keys written by another editor (an invoice draft started
        // from a quotation's lines), and what must not repeat is the number.
        const match = /(\d+)$/.exec(line.key);
        if (match === null) continue;
        const seen = Number(match[1]);
        if (Number.isSafeInteger(seen) && seen > counter) counter = seen;
      }
    },
  };
}

export function emptyLine(key: string): EditorLine {
  return {
    key,
    parentKey: null,
    isGroupHeader: false,
    itemId: null,
    description: "",
    // The default trade of this business: on-site fabrication IS a works
    // contract. The item picker overrides it from the catalogue row.
    supplyNature: "WORKS_CONTRACT",
    hsn: "",
    gstRatePercent: "18",
    pricingMode: "LUMPSUM",
    qty: "1",
    unitLabel: "NO",
    rate: "",
    pieces: "",
    weightKg: "",
    lengthM: "",
    discount: "",
    discountPercent: "",
    // The overwhelming majority of discounts this business gives are a round
    // rupee figure knocked off a quoted job, so rupees is what the box opens as.
    discountMode: "AMOUNT",
  };
}

/** Text that is meaningfully filled in — `undefined` for the API otherwise. */
export function text(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Every restored line, merged over a fresh empty one (§7.2).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE FIX THIS FILE'S CALLERS HAVE BEEN PREDICTING SINCE 2026-08-15.
 *
 * A draft's payload is whatever `EditorState` looked like ON THE DAY IT WAS
 * SAVED, and `document_drafts` has no migration for it. All three editors
 * already merge the payload over the empty form (`{ ...current, ...saved }`) so
 * a field added later arrives with its default — and all three carried a note
 * saying that "protects TOP-LEVEL keys only. A new field inside a LINE would
 * still arrive undefined on an old draft, because `lines` is replaced
 * wholesale... no such field has been added yet. Worth knowing before adding
 * one."
 *
 * `discount` (§15(3)(a)) is that field, and the prediction was exact: resuming
 * a draft saved yesterday threw `Cannot read properties of undefined (reading
 * 'trim')` inside `LineCard`, which unmounts the editor and shows a blank
 * crashed screen with the owner's typing still in the database.
 *
 * So this does per LINE what the spread already does per FORM. It is not
 * discount-specific and must not become so — the next field added to
 * `EditorLine` is covered by it without anyone remembering.
 *
 * THE PAYLOAD STILL WINS ON EVERY KEY IT HAS, including `parentKey: null` and
 * empty strings: JSON has no `undefined`, so a key the draft carries is present
 * and overrides, and only a genuinely absent one falls through to the default.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function adoptedLines(lines: readonly EditorLine[]): EditorLine[] {
  return lines.map((line) => ({ ...emptyLine(line.key), ...line }));
}

/**
 * A stored discount, back into the rupee string the form holds.
 *
 * BLANK FOR ZERO, never `"0"`. The box means "no discount" when it is empty, and
 * `toApiLine` / `toQuotationApiBody` then OMIT the key entirely rather than
 * sending a zero — which is what makes an undiscounted document post exactly
 * the body it posted before this field existed.
 *
 * `undefined` IS TREATED AS ZERO, and that is a different boundary from the one
 * {@link adoptedLines} guards: this reads a SERVER payload. `discountPaise` is
 * required on `DocumentLine` and the API always sends it, but the value reaching
 * here may be a TanStack cache entry written before the deploy, or a response
 * from an API not yet updated — and "Repeat Last Bill" reads a document that
 * could have been fetched either way. `paiseToInputValue(undefined)` yields
 * `"NaN"`, which then fails to parse and holds the whole line back, so the bill
 * silently refuses to save with nothing on screen saying why.
 *
 * Shared with the quotation editor, whose whole-document discount round-trips
 * through the same two rules.
 */
export function discountInput(paise: number | undefined): string {
  const discountPaise = paise ?? 0;
  return discountPaise === 0 ? "" : paiseToInputValue(discountPaise);
}

/**
 * A line the server can accept, or `null` when it is not yet complete enough to
 * send. Returning `null` rather than a partial object is what stops the preview
 * firing a 400 on every keystroke of a half-typed line.
 *
 * KIND-AGNOSTIC, AND THE QUOTATION EDITOR IMPORTS IT. `DocumentLineInput` is one
 * schema shared by both create routes (§4) — the document kinds differ in their
 * HEADER fields, never in a line. Exported for that reuse rather than copied.
 */
export function toApiLine(line: EditorLine): Record<string, unknown> | null {
  const description = line.description.trim();
  const hsn = line.hsn.trim();
  if (description === "" || hsn === "") return null;

  const gstRateBp = parsePercentToBasisPoints(line.gstRatePercent);
  if (gstRateBp === null) return null;

  const base: Record<string, unknown> = {
    key: line.key,
    parentKey: line.parentKey,
    isGroupHeader: line.isGroupHeader,
    itemId: line.itemId,
    description,
    supplyNature: line.supplyNature,
    hsn,
    gstRateBp,
  };

  if (line.isGroupHeader) {
    /*
     * A header carries no billable measurement — its rate is DERIVED from its
     * children by the engine (§4, §9.1). Sending a rate here is what produced
     * the real defect on invoice #66: a parent rate copy-pasted from #65 that
     * disagreed with the children beneath it.
     */
    return { ...base, pricingMode: "LUMPSUM", qtyMilli: 1000, unitLabel: "AU", ratePaise: 0 };
  }

  const qtyMilli = parseQtyToMilli(line.qty);
  const ratePaise = parseRupeesToPaise(line.rate);
  if (qtyMilli === null || ratePaise === null) return null;

  /*
   * §15(3)(a) — one key or the other or neither, decided in exactly one place.
   * `null` means the text will not parse, which holds the line back exactly as a
   * half-typed rate does.
   */
  const discount = discountBody(line);
  if (discount === null) return null;

  const measured: Record<string, unknown> = {
    ...base,
    pricingMode: line.pricingMode,
    qtyMilli,
    unitLabel: line.unitLabel.trim() === "" ? "NO" : line.unitLabel.trim(),
    ratePaise,
    ...discount,
  };

  // The measurement column the pricing mode requires. The schema refuses the
  // line without it, so an incomplete one is held back rather than sent.
  switch (line.pricingMode) {
    case "BY_PIECE": {
      const pieces = parseQtyToMilli(line.pieces);
      if (pieces === null) return null;
      return { ...measured, pieces: Math.trunc(pieces / 1000) };
    }
    case "BY_WEIGHT": {
      const weightGrams = parseKgToGrams(line.weightKg);
      if (weightGrams === null) return null;
      return { ...measured, weightGrams };
    }
    case "BY_LENGTH": {
      const lengthMm = parseMetresToMm(line.lengthM);
      if (lengthMm === null) return null;
      return { ...measured, lengthMm };
    }
    default:
      return measured;
  }
}

/**
 * The sendable lines of a document, or `null` when none of them are ready.
 *
 * A GROUP HEADER WITH NO COMPLETE CHILD IS DROPPED WITH ITS CHILDREN. Sending a
 * header whose children were all held back would ask the engine to derive a rate
 * from nothing, and the preview would show a job title with no money against it.
 * An ORPHAN — a child whose header was held back — goes the same way, or the
 * server would number it as a top-level line the owner never wrote.
 *
 * Shared with the quotation editor: see the note on {@link toApiLine}.
 */
export function usableApiLines(lines: EditorLine[]): Record<string, unknown>[] | null {
  const converted = new Map<string, Record<string, unknown>>();
  for (const line of lines) {
    const api = toApiLine(line);
    if (api !== null) converted.set(line.key, api);
  }

  const usable = [...converted.values()].filter((line) => {
    const parentKey = line.parentKey as string | null;
    return parentKey === null || converted.has(parentKey);
  });

  const withLiveChildren = usable.filter((line) => {
    if (line.isGroupHeader !== true) return true;
    return usable.some((other) => other.parentKey === line.key);
  });

  return withLiveChildren.length === 0 ? null : withLiveChildren;
}

/**
 * A discount as the API takes it — the ONE place either key is written.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT RETURNS AT MOST ONE KEY, AND THAT IS THE WHOLE CONTRACT.
 *
 * `discountPaise` and `discountPercentBp` are mutually exclusive on every
 * §15(3)(a) schema — a line's and a document's, on an invoice and on a quotation
 * — and sending both is a 400 carrying `DISCOUNT_PERCENT_AND_AMOUNT`: "two
 * answers would mean one of them being dropped without saying so". Returning a
 * single-key object rather than assembling two optional fields at four call
 * sites is what makes that structurally impossible here, and it is why the mode
 * is read FIRST: the other box may hold stale text and is simply never looked at.
 *
 * `{}` FOR A BLANK BOX. The key is OMITTED, never sent as a zero — absent is not
 * "a discount of zero", it is the request an undiscounted document has always
 * sent, and it takes the same path through the engine.
 *
 * `null` MEANS THE TEXT WILL NOT PARSE, and the caller holds the whole request
 * back rather than previewing a document without a reduction the owner typed.
 *
 * A PERCENTAGE CROSSES THE WIRE AS BASIS POINTS AND AS NOTHING ELSE — `10` →
 * `1000`, through the same integer-safe `parseScaled` every rate uses, so no
 * float is ever constructed. NO MONEY IS COMPUTED HERE and none may be: the
 * server resolves the percentage against the line's (or the document's) own base
 * through `divRoundHalfUp`, once, at save. A "quick" multiplication here would
 * be a second tax engine, and the day it rounded differently the customer would
 * find out first (CLAUDE.md, §6).
 *
 * NOR IS THE PERCENTAGE RANGE-CHECKED. Above 100% is more than the thing it
 * comes off, and `DiscountPercentBpSchema` refuses it with a sentence written
 * for the owner; the totals bar shows that sentence. Re-stating the bound here
 * would mean two wordings of one rule, and the client's would be the one nobody
 * updated.
 *
 * SHARED BY BOTH EDITORS AND BY BOTH LEVELS — a line, and the whole document.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function discountBody(fields: DiscountFields): Record<string, number> | null {
  const typed = typedDiscount(fields);
  if (typed === "") return {};

  if (fields.discountMode === "PERCENT") {
    const discountPercentBp = parsePercentToBasisPoints(typed);
    return discountPercentBp === null ? null : { discountPercentBp };
  }

  const discountPaise = parseRupeesToPaise(typed);
  return discountPaise === null ? null : { discountPaise };
}

/**
 * The text that IS this discount right now, trimmed — the box the mode does not
 * name is not read at all.
 *
 * Exists so that "is there a discount on this line?" is asked in ONE place. The
 * line card changes its amount caption to "After discount" on the strength of
 * it, and a second copy of the mode check would keep saying "Amount" on a line
 * discounted by percentage.
 */
export function typedDiscount(fields: DiscountFields): string {
  return (fields.discountMode === "PERCENT" ? fields.discountPercent : fields.discount).trim();
}

/**
 * The whole INVOICE as the API takes it, or `null` when it is not sendable yet.
 */
export function toApiBody(state: EditorState): Record<string, unknown> | null {
  if (state.customerId === null || state.date.trim() === "") return null;

  const discount = discountBody(state);
  if (discount === null) return null;

  const withLiveChildren = usableApiLines(state.lines);
  if (withLiveChildren === null) return null;

  return {
    ...discount,
    customerId: state.customerId,
    date: state.date,
    soNumber: text(state.soNumber),
    soDate: text(state.soDate),
    challanNumber: text(state.challanNumber),
    challanDate: text(state.challanDate),
    quotationRef: text(state.quotationRef),
    quotationRefDate: text(state.quotationRefDate),
    attnName: text(state.attnName),
    notes: text(state.notes),
    terms: text(state.terms),
    /*
     * OMITTED WHEN NOTHING IS TICKED, never sent as `[]`. `challanIds` is
     * `.min(1, "Select at least one delivery challan, or leave the field
     * empty")`, so an empty array is a 400 on every bill that closes no
     * delivery — which is most of them.
     *
     * Spelled `challanIds`, and the spelling is load-bearing: `InvoiceCreateSchema`
     * is a plain `z.object()`, so unknown keys are STRIPPED rather than refused.
     * A typo'd `challanIDs` would never error — it would simply fail to close
     * anything, silently, for ever.
     */
    ...(state.challanIds.length === 0 ? {} : { challanIds: state.challanIds }),
    lines: withLiveChildren,
  };
}

/**
 * The server's figures for the document as it currently stands.
 *
 * DEBOUNCED, because it runs the real tax pipeline on every change. 400ms is
 * long enough that typing a rate does not fire six requests and short enough
 * that the total feels attached to the keystroke.
 */
export function useInvoicePreview(state: EditorState) {
  const body = toApiBody(state);
  const [debounced, setDebounced] = useState<string | null>(null);

  const serialised = body === null ? null : JSON.stringify(body);

  useEffect(() => {
    if (serialised === null) {
      setDebounced(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setDebounced(serialised);
    }, 400);
    return () => {
      window.clearTimeout(timer);
    };
  }, [serialised]);

  return useQuery({
    queryKey: ["invoices", "preview", debounced],
    queryFn: () =>
      api.post<PreviewEnvelope<DocumentPreview>>(
        endpoints.invoicePreview,
        JSON.parse(debounced ?? "{}") as unknown,
      ),
    enabled: debounced !== null,
    // The last good figures stay on screen while the next preview is in flight,
    // so the total does not blink to nothing between keystrokes.
    placeholderData: keepPreviousData,
    // A rejected preview is information, not a transient failure to retry: the
    // document is incomplete and the message says how.
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** `POST /invoices` — the save. Consumes a number, and cannot be undone (§6). */
export function useCreateInvoice(mode: "new" | "record" = "new") {
  const queryClient = useQueryClient();

  return useMutation({
    // §6.1 — record-manual takes the number off the paper and moves the counter
    // past it; the ordinary create allocates the next one. Same envelope back.
    mutationFn: async (body: Record<string, unknown>) =>
      api.post<PreviewEnvelope<InvoiceDetail>>(
        mode === "record" ? endpoints.invoiceRecordManual : endpoints.invoices,
        body,
      ),
    onSuccess: () => {
      /*
       * EVERYTHING. A new bill changes the register, the home screen's "billed
       * this month", the chart's column, the last-bill card and the recent list.
       * A narrower invalidation leaves the owner looking at a total that does
       * not include the bill he just saved, which reads as the save having
       * failed.
       */
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * One customer, whole — the hydration behind a resumed draft or a copied bill.
 *
 * A draft stores only `customerId` (plus a display name in its summary), and a
 * copied invoice carries a frozen snapshot; neither is the full row the chosen-
 * customer card renders. Restoring `{id, name}` alone made the card claim
 * "No GSTIN" and show the IGST hint for a Maharashtra buyer — the tax stayed
 * correct (the server derives everything from the id), but the card lied about
 * it on the screen where a lie matters most.
 */
export function useCustomerById(id: number | null) {
  return useQuery({
    queryKey: ["customers", "detail", id === null ? "none" : String(id)],
    enabled: id !== null,
    queryFn: async () => api.get<{ data: Customer }>(endpoints.customer(id ?? 0)),
    select: (envelope) => envelope.data,
  });
}

/** Customers for the picker — searched server-side by name, phone or GSTIN. */
export function useCustomerSearch(q: string) {
  const trimmed = q.trim();
  const search = new URLSearchParams({ limit: "20", active: "true" });
  if (trimmed !== "") search.set("q", trimmed);

  return useQuery({
    queryKey: queryKeys.customers.list({ q: trimmed, active: true, limit: 20 }),
    queryFn: () => api.get<CustomerListResponse>(`${endpoints.customers}?${search.toString()}`),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/**
 * Saved jobs for the line picker.
 *
 * With no search text this is the MOST-USED list, because the server orders by
 * `usage_count` — §11.1's "the jobs he bills every week are one tap away". A
 * fabricator bills the same dozen jobs over and over.
 */
export function useItemSearch(q: string) {
  const trimmed = q.trim();
  const search = new URLSearchParams({ limit: "20", active: "true" });
  if (trimmed !== "") search.set("q", trimmed);

  return useQuery({
    queryKey: queryKeys.items.list({ q: trimmed, active: true, limit: 20 }),
    queryFn: () => api.get<ItemListResponse>(`${endpoints.items}?${search.toString()}`),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/**
 * Fifty, matching the register's page size and the server's `challanIds` ceiling
 * (`.max(50, "That is more deliveries than one invoice can close")`). One buyer
 * with more than fifty unbilled deliveries is a conversation, not a bill.
 */
const BILLABLE_CHALLAN_LIMIT = 50;

/**
 * The deliveries THIS customer is waiting to be billed for (§17.7).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT ASKS THE SERVER FOR EXACTLY THE SET THE SAVE WILL ACCEPT, and that is the
 * whole design. `openOnly` is `unbilledChallanWhere()` on the API side — OPEN,
 * no linked invoice, and a BILLABLE challan type — which is the same predicate
 * `prepareChallanLinks` checks the posted `challanIds` against before the
 * invoice number is allocated. Filtering client-side from a wider list, or
 * re-stating "billable" here, would let the picker offer a job-work movement
 * the save then refuses with a 409 the owner cannot act on.
 *
 * `customerId` IS NOT OPTIONAL IN PRACTICE. Rule 2 refuses another buyer's
 * challan, so a picker not narrowed to the customer being billed would list
 * deliveries that can only produce that refusal. The query simply does not run
 * until a customer is chosen.
 *
 * A 404 IS "THE FEATURE IS OFF", NOT "NO SUCH THING". Every challan route sits
 * behind `requireFlag("challans")`, which answers 404 rather than 403 so a
 * "forbidden" cannot leak unreleased work (§18.3). There is no `useFlag` on the
 * client and this hook does not invent one: the caller renders nothing when the
 * query has no rows, and an error has no rows either — which is the same
 * outcome the flag wants and needs no branch of its own.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useBillableChallans(customerId: number | null) {
  const search = new URLSearchParams({
    openOnly: "true",
    limit: String(BILLABLE_CHALLAN_LIMIT),
  });
  if (customerId !== null) search.set("customerId", String(customerId));

  return useQuery({
    /*
     * `challanKeys`, imported rather than spelled again, so that saving or
     * cancelling a challan invalidates this list too — a delivery billed on
     * another device must stop being offered here.
     */
    queryKey: challanKeys.list({
      openOnly: true,
      customerId: customerId ?? undefined,
      limit: BILLABLE_CHALLAN_LIMIT,
    }),
    enabled: customerId !== null,
    queryFn: () => api.get<ChallanListResponse>(`${endpoints.challans}?${search.toString()}`),
    /*
     * A 404 here is the feature switched off and a 403 is a capability the user
     * does not hold; neither becomes true by asking again, and the picker
     * renders nothing either way. Retrying would only delay the empty state.
     */
    retry: false,
    staleTime: 30_000,
  });
}
